import fs from "node:fs";
import path from "node:path";
import { spawn } from "node:child_process";
import { configDir } from "../../auth/config";
import { detectChannel, type Channel } from "../build-info";
import { hasStructuredOutputFlag } from "../colorize-output";

/**
 * Background update check, modeled on update-notifier: never block or slow a
 * command. Each run synchronously reads a small cache file; if a newer version
 * was recorded by a previous check, a notice is printed to stderr when the
 * process exits. When the cache is stale (>1h) a detached `whop upgrade`
 * child refreshes it (and, for standalone installs, applies the update) in the
 * background — the current invocation never waits on the network.
 */

export const DEFAULT_MANIFEST_URL =
	"https://github.com/whopio/whop-public-cli/releases/latest/download/manifest.json";

export function manifestUrl(env: NodeJS.ProcessEnv = process.env): string {
	return env.WHOP_CLI_MANIFEST_URL?.trim() || DEFAULT_MANIFEST_URL;
}

const CHECK_INTERVAL_MS = 60 * 60 * 1000;

export interface UpdateCache {
	lastCheckedAt?: string;
	latestVersion?: string;
	/** Set by a background self-update so the next run can announce it. */
	updatedTo?: string;
}

function cachePath(env: NodeJS.ProcessEnv): string {
	return path.join(configDir(env), "update-check.json");
}

export function readUpdateCache(
	env: NodeJS.ProcessEnv = process.env,
): UpdateCache {
	try {
		const parsed = JSON.parse(
			fs.readFileSync(cachePath(env), "utf8"),
		) as unknown;
		if (
			parsed != null &&
			typeof parsed === "object" &&
			!Array.isArray(parsed)
		) {
			return parsed as UpdateCache;
		}
	} catch {
		// missing or corrupt cache — same as never checked
	}
	return {};
}

export function writeUpdateCache(
	cache: UpdateCache,
	env: NodeJS.ProcessEnv = process.env,
): void {
	try {
		fs.mkdirSync(configDir(env), { recursive: true, mode: 0o700 });
		fs.writeFileSync(cachePath(env), `${JSON.stringify(cache, null, 2)}\n`, {
			mode: 0o600,
		});
	} catch {
		// a failed cache write must never break the command being run
	}
}

export function isNewerVersion(candidate: string, current: string): boolean {
	const parse = (v: string) =>
		v
			.replace(/^v/, "")
			.split("-")[0]
			.split(".")
			.map((part) => Number.parseInt(part, 10) || 0);
	const a = parse(candidate);
	const b = parse(current);
	for (let i = 0; i < Math.max(a.length, b.length); i++) {
		if ((a[i] ?? 0) > (b[i] ?? 0)) return true;
		if ((a[i] ?? 0) < (b[i] ?? 0)) return false;
	}
	return false;
}

export function upgradeCommandFor(channel: Channel): string {
	switch (channel) {
		case "npm":
			return "npm install -g @whop/cli";
		case "brew":
			return "brew upgrade whop";
		default:
			return "whop upgrade";
	}
}

/** True when update noise would corrupt output or is unwanted: agents, CI, pipes, opt-out. */
export function updateCheckSuppressed(
	argv: string[],
	env: NodeJS.ProcessEnv = process.env,
	stderrIsTty: boolean = process.stderr.isTTY ?? false,
): boolean {
	if (env.WHOP_CLI_NO_UPDATE_CHECK) return true;
	if (env.CI) return true;
	if (!stderrIsTty) return true;
	// Any machine-output flag means the caller is parsing the output (incur's
	// human rendering is the no-flag TTY default). Reuses the predicate that
	// also disables colorization, so the two stay in sync.
	if (hasStructuredOutputFlag(argv)) return true;
	const first = argv[0];
	if (
		first === "upgrade" ||
		first === "mcp" ||
		first === "completions" ||
		first?.startsWith("--")
	) {
		return true;
	}
	return false;
}

function spawnBackgroundCheck(): void {
	const args =
		detectChannel() === "standalone" || detectChannel() === "brew"
			? ["upgrade", "--background"]
			: [process.argv[1], "upgrade", "--background"];
	try {
		spawn(process.execPath, args, {
			detached: true,
			stdio: "ignore",
		}).unref();
	} catch {
		// background check is best-effort
	}
}

export function scheduleUpdateNotice(
	argv: string[],
	currentVersion: string,
	env: NodeJS.ProcessEnv = process.env,
): void {
	const channel = detectChannel(env);
	if (channel === "dev") return;
	if (updateCheckSuppressed(argv, env)) return;

	const cache = readUpdateCache(env);

	if (cache.updatedTo && cache.updatedTo === currentVersion) {
		writeUpdateCache({ ...cache, updatedTo: undefined }, env);
		process.on("exit", () => {
			process.stderr.write(
				`\nwhop was updated to v${currentVersion} automatically.\n`,
			);
		});
		return;
	}

	if (
		cache.latestVersion &&
		isNewerVersion(cache.latestVersion, currentVersion)
	) {
		const latest = cache.latestVersion;
		process.on("exit", () => {
			process.stderr.write(
				`\nA new version of whop is available: v${currentVersion} → v${latest}\nRun \`${upgradeCommandFor(channel)}\` to update.\n`,
			);
		});
	}

	const lastChecked = cache.lastCheckedAt
		? new Date(cache.lastCheckedAt).getTime()
		: 0;
	if (Date.now() - lastChecked > CHECK_INTERVAL_MS) {
		writeUpdateCache(
			{ ...cache, lastCheckedAt: new Date().toISOString() },
			env,
		);
		spawnBackgroundCheck();
	}
}
